import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import fr from "./translations/fr";
import en from "./translations/en";

const DICTS = { fr, en };
const SUPPORTED = ["fr", "en"];
const STORAGE_KEY = "wedo:lang";

const LanguageContext = createContext({
  lang: "fr",
  setLang: () => {},
  t: () => "",
  available: SUPPORTED,
  isEnglishReady: true,
});

/** Resolve dot-path keys against a dictionary. Returns the raw value (string / object / array). */
function resolve(dict, path) {
  return path.split(".").reduce((acc, k) => (acc != null ? acc[k] : undefined), dict);
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    if (typeof window === "undefined") return "fr";
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return SUPPORTED.includes(stored) ? stored : "fr";
  });

  useEffect(() => {
    if (typeof document !== "undefined") document.documentElement.lang = lang;
    if (typeof window !== "undefined") window.localStorage.setItem(STORAGE_KEY, lang);
  }, [lang]);

  const setLang = useCallback((next) => {
    if (SUPPORTED.includes(next)) setLangState(next);
  }, []);

  const t = useCallback(
    (path, fallback = "") => {
      const value = resolve(DICTS[lang] || DICTS.fr, path);
      if (value != null) return value;
      const fr_value = resolve(DICTS.fr, path);
      if (fr_value != null) return fr_value;
      return fallback; // never echo the dot path back to UI
    },
    [lang]
  );

  const value = useMemo(
    () => ({ lang, setLang, t, available: SUPPORTED, isEnglishReady: true }),
    [lang, setLang, t]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  return useContext(LanguageContext);
}

/** Convenience hook: `const t = useT(); t('home.hero.line1')` */
export function useT() {
  return useContext(LanguageContext).t;
}
